import { CoverageOption, formatVIN, Tractor } from "@deathstar/types/northstar";
import { DriverRow, EquipmentRow } from "@deathstar/types/waypoint";
import { ActionButton, DataTable, fuzzyFilter, Tabs, useSnackbar } from "@deathstar/ui";
import { PlusIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { CircularProgress } from "@material-ui/core";
import { useMutation } from "@tanstack/react-query";
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";
import moment from "moment";
import { useMemo, useState } from "react";
import { animated, useSpring } from "react-spring";
import api from "../../api/api";
import { useAccountId } from "../../api/useAccountId";
import Loader from "../../components/loader/Loader";
import { queryClient } from "../../util/queryClient";
import { DriverLinker } from "../drivers/DriverLinker";
import { OwnerOperatorEmployerLinker } from "../drivers/OwnerOperatorEmployerLinker";
import {
    getPendingEquipmentChanges,
    hasPendingRemoval,
    getPendingEquipmentPolicyChangePriority,
    getPendingPolicyChangeRowClass,
    isPendingPolicyAdditionRow,
    usePendingPolicyChanges,
    withPendingEquipmentAdditions,
} from "../pending-policy-changes/PendingPolicyChanges";
import EquipmentColumns, { EquipmentTableMeta } from "./EquipmentColumns";
import { NewUnitDialog } from "./NewUnitDialog";

const AnimatedDataTable = animated(DataTable<EquipmentRow<Tractor>>);

export default function TractorsTable({ data }: { data: EquipmentRow<Tractor>[] | undefined }) {
    const accountId = useAccountId();
    const [filter, setFilter] = useState<"all" | "company" | "owner-operator">("all");
    const [adding, setAdding] = useState(false);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [linkingTractorToDriverId, setLinkingTractorToDriverId] = useState<number | null>(null);
    const [linkingEmployerToTractorId, setLinkingEmployerToTractorId] = useState<number | null>(null);
    const updateUnit = api.equipment.useUpdate("tractors", accountId!);
    const pendingPolicyChanges = usePendingPolicyChanges();

    const memoizedData = useMemo(() => data || [], [data]);
    const tableData = useMemo(
        () => withPendingEquipmentAdditions(memoizedData, pendingPolicyChanges.changes, accountId!, "tractors"),
        [accountId, memoizedData, pendingPolicyChanges.changes]
    );

    const spring = useSpring({
        from: { opacity: 0 },
        to: { opacity: 1 },
        pause: !data,
    });

    const sortingState = useMemo(() => {
        return [{ id: "pending", desc: true }, ...sorting];
    }, [sorting]);

    const columnFilters = useMemo(() => {
        if (filter === "all") {
            return [];
        }
        return [{ id: "owner-operator", value: filter === "owner-operator" }];
    }, [filter]);

    const meta: EquipmentTableMeta = useMemo(
        () => ({
            openDriverLinker(driverId: number) {
                setLinkingTractorToDriverId(driverId);
            },
            openEmployerLinker(tractorId: number) {
                setLinkingEmployerToTractorId(tractorId);
            },
            isPendingRemoval(row: { accountId: string; id: number }) {
                return hasPendingRemoval(getPendingEquipmentChanges(pendingPolicyChanges.changes, row.accountId, "tractors", row.id));
            },
        }),
        [pendingPolicyChanges.changes]
    );

    const columns = useMemo(() => {
        return (
            [
                EquipmentColumns.mobileTractorCell,
                EquipmentColumns.tractorBrowserPending,
                {
                    ...EquipmentColumns.pending,
                    accessorFn: (row: EquipmentRow<Tractor>) =>
                        getPendingEquipmentPolicyChangePriority(pendingPolicyChanges.changes, row, "tractors"),
                },
                EquipmentColumns.upcomingDate,
                EquipmentColumns.unitNumber,
                EquipmentColumns.year,
                EquipmentColumns.make,
                EquipmentColumns.type,
                EquipmentColumns.vin,
                EquipmentColumns.value,
                EquipmentColumns.ownerOperator,
                EquipmentColumns.coverages,
                EquipmentColumns.additionalInterests,
                EquipmentColumns.employerColumn,
                EquipmentColumns.linkedDriversColumn,
                tableData.some((t) => t.ownerOperatorAPDFirstNamedInsureds?.length)
                    ? EquipmentColumns.ownerOperatorAPDFirstNamedInsureds
                    : null,
                EquipmentColumns.tractorActions,
            ] as ColumnDef<EquipmentRow<Tractor>>[]
        ).filter(Boolean);
    }, [pendingPolicyChanges.changes, tableData]);

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        globalFilterFn: fuzzyFilter,
        state: {
            sorting: sortingState,
            columnFilters,
        },
        initialState: {
            pagination: {
                pageSize: 30,
            },
        },
        onSortingChange: setSorting,
        meta,
    });

    const downloadCSV = useMutation({
        mutationFn: async () => {
            const tableData = memoizedData.filter((r) => {
                switch (filter) {
                    case "company":
                        return !r.policyOwnerOperator;
                    case "owner-operator":
                        return r.policyOwnerOperator;
                    default:
                        return true;
                }
            });

            if (tableData.length === 0) {
                useSnackbar.add("No data to export", { variant: "info" });
                return;
            }

            const drivers = await api.drivers.find(tableData[0].accountId);

            const rows: string[] = [];
            tableData.forEach((row) => {
                rows.push(
                    [
                        row.unitNumber,
                        row.year,
                        row.make,
                        row.type?.name,
                        row.vin,
                        row.policyAcv ? "ACV" : row.policyValue,
                        row.policyOwnerOperator ? "IC Owned" : "Company",
                        row.coverages
                            ?.filter((c) => !c.isUpcoming)
                            .map((c) => CoverageOption.getMetadata(c.coverageOptionId).defaultAbbreviation)
                            .join(",") || "",
                        row.drivers
                            ?.map((d1) => drivers.find((d2) => d1.id === d2.id) as DriverRow)
                            .filter(Boolean)
                            .map((d) => d.name)
                            .join(",") || "",
                        tableData.some((t) => t.ownerOperatorAPDFirstNamedInsureds?.length)
                            ? row.ownerOperatorAPDFirstNamedInsureds?.join(",")
                            : "",
                    ]
                        .map((x) => `"${x ?? ""}"`)
                        .join(",")
                );
            });

            const csv = [
                [
                    "Unit Number",
                    "Year",
                    "Make",
                    "Type",
                    "VIN",
                    "Value",
                    "Owner Type",
                    "Coverage",
                    "Drivers",
                    tableData.some((t) => t.ownerOperatorAPDFirstNamedInsureds?.length) ? "Named Insured (APD)" : "",
                ].join(","),
                ...rows,
            ].join("\n");

            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.download = "tractors.csv";
            a.href = url;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
    });

    return (
        <div>
            {data ? (
                <>
                    <AnimatedDataTable
                        style={spring}
                        table={table}
                        getRowClassName={(row) =>
                            isPendingPolicyAdditionRow(row)
                                ? "bg-emerald-100/80"
                                : getPendingPolicyChangeRowClass(
                                      getPendingEquipmentChanges(pendingPolicyChanges.changes, row.accountId, "tractors", row.id)
                                  )
                        }
                        headerRow={
                            <div className="flex flex-wrap items-end gap-4">
                                <Tabs<typeof filter>
                                    tabs={[
                                        {
                                            label: () => <p className="px-2 py-1 md:w-32">All tractors</p>,
                                            value: "all",
                                            buttonProps: { className: "z-[1]" },
                                        },
                                        {
                                            label: () => <p className="px-2 py-1 md:w-32">Company owned</p>,
                                            value: "company",
                                            buttonProps: { className: "z-[1]" },
                                        },
                                        {
                                            label: () => <p className="px-2 py-1 md:w-32">IC owned</p>,
                                            value: "owner-operator",
                                            buttonProps: { className: "z-[1]" },
                                        },
                                    ]}
                                    value={filter}
                                    onChange={setFilter}
                                    className="rounded-md bg-stone-100 py-1 shadow-inner"
                                    borderClasses="bg-white border border-waypoint-blue z-[0] shadow rounded-md"
                                />
                                <div className="grow"></div>
                                <ActionButton disabled={downloadCSV.isPending} onClick={() => downloadCSV.mutate()}>
                                    {downloadCSV.isPending ? <CircularProgress size="1rem" /> : <TableCellsIcon className="h-5 w-5" />}
                                    Export
                                </ActionButton>
                                <ActionButton onClick={() => setAdding(true)}>
                                    <PlusIcon className="h-5 w-5" />
                                    Add tractor
                                </ActionButton>
                                <DataTable.Search table={table} />
                            </div>
                        }
                    />
                    <NewUnitDialog
                        open={adding}
                        onClose={() => setAdding(false)}
                        unitType="tractors"
                        defaultValues={{ atDate: moment().format("YYYY-MM-DD") }}
                        getExistingUnit={(vin) => memoizedData.find((r) => formatVIN(r.vin) === formatVIN(vin)) as Tractor}
                        deferPolicyChanges
                    />
                    <DriverLinker
                        open={!!linkingTractorToDriverId}
                        currentValue={memoizedData.find((r) => r.id === linkingTractorToDriverId)?.drivers || []}
                        onClose={() => setLinkingTractorToDriverId(null)}
                        onChange={async (drivers) => {
                            if (linkingTractorToDriverId) {
                                updateUnit.mutate({ id: linkingTractorToDriverId, drivers });
                                setLinkingTractorToDriverId(null);
                            }
                        }}
                    />
                    <OwnerOperatorEmployerLinker
                        open={!!linkingEmployerToTractorId}
                        currentValue={memoizedData.find((r) => r.id === linkingEmployerToTractorId)?.employerId ?? null}
                        onClose={() => setLinkingEmployerToTractorId(null)}
                        onChange={(employer) => {
                            if (linkingEmployerToTractorId) {
                                updateUnit.mutate(
                                    { id: linkingEmployerToTractorId, employer },
                                    {
                                        onSuccess: () =>
                                            queryClient.invalidateQueries({
                                                queryKey: api.ownerOperatorEmployers.queryKeys.get(accountId!),
                                            }),
                                    }
                                );
                                setLinkingEmployerToTractorId(null);
                            }
                        }}
                    />
                </>
            ) : (
                <Loader />
            )}
        </div>
    );
}
